import {useCallback, useEffect, useMemo, useState} from 'react'
import {
  useTable,
  UseSortByColumnProps,
  ColumnInstance,
  Row,
  useSortBy,
  TableInstance,
} from 'react-table'
import {KTCard, KTCardBody, useDebounce} from '../../../_metronic/helpers'
import {Loader} from '../product-master/loader'
import {getReturnColumns} from './columns'
import {ReturnRecord, ProductOption, getReturns, getProductOptions} from './api'
import clsx from 'clsx'
import {useLayout} from '../../../_metronic/layout/core'
import {KTIcon} from '../../../_metronic/helpers'
import {CreateReturn} from './create-return'
import {Modal} from 'react-bootstrap'

// ─── Types ────────────────────────────────────────────────────────────────────

type SortableColumn = ColumnInstance<ReturnRecord> & UseSortByColumnProps<ReturnRecord>
type TableInstanceWithHooks = TableInstance<ReturnRecord> & {
  headers: Array<ColumnInstance<ReturnRecord> & UseSortByColumnProps<ReturnRecord>>
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const ReturnsManagement = () => {
  const {company} = useLayout()

  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [createReturn, setCreateReturn] = useState(false)
  const [data, setData] = useState<ReturnRecord[]>([])
  const [productMap, setProductMap] = useState<Record<number, string>>({})
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [lastCompanyId, setLastCompanyId] = useState<string | null>(null)
  const [shouldRefreshReturns, setShouldRefreshReturns] = useState(true)

  const columns = useMemo(() => getReturnColumns(productMap), [productMap])

  const debouncedSearchTerm = useDebounce(searchTerm, 150)

  const filteredData: ReturnRecord[] = useMemo(() => {
    if (debouncedSearchTerm?.length) {
      return data.filter(
        (d) =>
          d.return_type.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          d.reason.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          d.processed_by.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          (productMap[d.product_id] ?? '').toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    }
    return []
  }, [debouncedSearchTerm, data, productMap])

  const {getTableProps, getTableBodyProps, headers, rows, prepareRow} = useTable(
    {
      columns,
      data: debouncedSearchTerm?.length ? filteredData : data,
    },
    useSortBy
  ) as TableInstanceWithHooks

  const getAllReturns = useCallback(async () => {
    if (company?.value) {
      try {
        setIsError(false)
        setIsLoading(true)
        const [returnsRes, productsRes] = await Promise.all([
          getReturns(company.value),
          getProductOptions(company.value),
        ])
        returnsRes && setData(returnsRes)
        if (productsRes) {
          const map: Record<number, string> = {}
          productsRes.forEach((p: ProductOption) => {
            map[p.product_id] = p.product_name
          })
          setProductMap(map)
        }
        setLastCompanyId(company.value)
      } catch {
        setIsError(true)
      } finally {
        setIsLoading(false)
      }
    }
  }, [company])

  useEffect(() => {
    if (!createReturn && shouldRefreshReturns) {
      getAllReturns()
    }
  }, [getAllReturns, createReturn, shouldRefreshReturns])

  // Separate effect to handle company changes
  useEffect(() => {
    if (company?.value && company.value !== lastCompanyId) {
      getAllReturns()
    }
  }, [company?.value, getAllReturns, lastCompanyId])

  return (
    <>
      {/* Toolbar Card */}
      <KTCard className='mb-5'>
        <div className='card-header border-0 justify-content-end'>
          <div className='card-toolbar'>
            <div className='d-flex gap-2' data-kt-user-table-toolbar='base'>
              <button
                type='button'
                className='btn p-0'
                style={{
                  backgroundColor: 'var(--bs-primary)',
                  color: 'var(--bs-primary-inverse)',
                  display: 'inline-flex',
                  height: '43.59px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                }}
                onClick={() => setCreateReturn(true)}
              >
                <span className='px-5'>Process Return</span>
                <span
                  style={{
                    color: '#fff',
                    backgroundColor: '#006060',
                    padding: '0 15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    borderTopRightRadius: 'inherit',
                    borderBottomRightRadius: 'inherit',
                  }}
                >
                  <KTIcon iconName='plus' className='fs-2 text-white' />
                </span>
              </button>
            </div>
          </div>
        </div>
      </KTCard>

      {/* Table Card */}
      <KTCard>
        <>
          <div className='card-header border-0 pt-6'>
            <div className='card-title'>
              {/* begin::Search */}
              <div className='d-flex align-items-center position-relative my-1'>
                <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
                <input
                  type='text'
                  data-kt-user-table-filter='search'
                  className='form-control form-control-solid w-250px ps-14'
                  placeholder='Search'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {/* end::Search */}
            </div>
          </div>

          <KTCardBody className='py-4'>
            {!isLoading && (
              <div className='table-responsive'>
                <table
                  className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
                  {...getTableProps()}
                >
                  <thead>
                    <tr className='text-start text-muted fw-bolder bg-light ps-4 fs-7 text-uppercase gs-0'>
                      {headers.map((column: SortableColumn) => (
                        <CustomHeaderColumn key={column.id} column={column} />
                      ))}
                    </tr>
                  </thead>
                  <tbody className='text-gray-600 text-center fw-bold' {...getTableBodyProps()}>
                    {rows.length > 0 ? (
                      rows.map((row: Row<ReturnRecord>, i) => {
                        prepareRow(row)
                        return <CustomRow row={row} key={`row-${i}-${row.id}`} />
                      })
                    ) : (
                      <tr>
                        <td colSpan={8}>
                          <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                            No matching records found
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {isLoading && <Loader isError={isError} />}
          </KTCardBody>

          <Modal
            className='modal-sticky modal-sticky-lg modal-sticky-bottom-right'
            role='dialog'
            data-backdrop='false'
            aria-hidden='true'
            tabIndex='-1'
            key={Date.now()}
            show={createReturn}
            animation={true}
            size='xl'
            contentClassName='top-0'
          >
            <CreateReturn
              key={Date.now()}
              setCreateReturn={setCreateReturn}
              setShouldRefreshReturns={setShouldRefreshReturns}
            />
          </Modal>
        </>
      </KTCard>
    </>
  )
}

// ─── Table Sub-components ─────────────────────────────────────────────────────

type ColProps = {
  column: ColumnInstance<ReturnRecord> & UseSortByColumnProps<ReturnRecord>
}

export const CustomHeaderColumn: React.FC<ColProps> = ({column}) => (
  <>
    {column.Header && typeof column.Header === 'string' ? (
      <th {...column.getHeaderProps(column.getSortByToggleProps())} className='cursor-pointer'>
        {column.render('Header')}
        <span className='ms-1'>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
      </th>
    ) : (
      column.render('Header')
    )}
  </>
)

type RowProps = {
  row: Row<ReturnRecord>
}

const CustomRow: React.FC<RowProps> = ({row}) => (
  <tr {...row.getRowProps()} className='bg-hover-light'>
    {row.cells.map((cell) => {
      return (
        <td
          {...cell.getCellProps()}
          className={clsx({'text-end min-w-100px': cell.column.id === 'actions'})}
        >
          {cell.render('Cell')}
        </td>
      )
    })}
  </tr>
)
